import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import React, { useEffect, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import {
  getMMdndEvents,
  MMdndContext,
} from '../../../../../common/helpers/MMdndContext';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../common/widgets/common/PathGraph/PathGraph';
import { StartBotsDialogueEditor } from './StartBotsDialogueEditor';
import { WidgetAlerts } from './WidgetByType/WidgetByType';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import AdConfigModel from '@mm/state/model/AdConfigModel';

interface Props {
  dialogueId: number | undefined;
  className?: string;
  usedInSlideout?: boolean;
  widgetsToExclude?: WidgetTypes[];
  forInstagram?: boolean;
  platforms?: PlatformType[];
  showAttributesStartWarning?: boolean;
  firstWidgetNotAllowedWarning?: WidgetAlerts;
  disableFirstWidgetConditionalLogic?: boolean;
  forAds?: boolean;
  adConfig?: AdConfigModel | null;
  isDisabled?: boolean;
}

export const EmbeddableDialogueEditor = (props: Props) => {
  const {
    dialogueId,
    className,
    usedInSlideout,
    widgetsToExclude,
    forInstagram,
    platforms,
    showAttributesStartWarning,
    firstWidgetNotAllowedWarning,
    disableFirstWidgetConditionalLogic,
    forAds,
    adConfig,
    isDisabled,
  } = props;
  const pageCRUDActions = usePageCRUDActions();
  const widgetCRUDActions = useWidgetCRUDActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogueId, state.widget.entities),
  );
  const dndEvents = getMMdndEvents();

  const pathGraph = useNewGraphInstance(dialogueId);

  const [loadingWidgets, setLoadingWidgets] = useState(true);
  useEffect(() => {
    (async () => {
      if (dialogueId) {
        setLoadingWidgets(true);
        await Promise.all([
          pageCRUDActions.retrieveById(dialogueId),
          widgetCRUDActions.retrieveAll(dialogueId, true),
        ]);
        setLoadingWidgets(false);
      }
    })();
  }, [dialogueId, pageCRUDActions, widgetCRUDActions]);

  if (!dialogueId) {
    return null;
  }

  return (
    <MMdndContext.Provider value={dndEvents}>
      <DragDropContext
        onDragStart={dndEvents.onDragStart}
        onDragUpdate={dndEvents.onDragUpdate}
        onDragEnd={dndEvents.onDragEnd}
      >
        <PathGraphContext.Provider value={pathGraph}>
          {loadingWidgets && (
            <div>
              <i className="fas fa-spinner fa-spin" />

              <span>Retrieving widgets...</span>
            </div>
          )}
          <StartBotsDialogueEditor
            className={className}
            chatbotId={chatbotId}
            widgets={widgets}
            dialogueId={dialogueId}
            usedInSlideout={usedInSlideout}
            widgetsToExclude={widgetsToExclude}
            forInstagram={forInstagram}
            showAttributesStartWarning={showAttributesStartWarning}
            firstWidgetNotAllowedWarning={firstWidgetNotAllowedWarning}
            platforms={platforms}
            disableFirstWidgetConditionalLogic={
              disableFirstWidgetConditionalLogic
            }
            forAds={forAds}
            isDisabled={isDisabled}
            hideTopWidgetAdder={forAds}
            disableFirstWidgetDeleting={forAds}
            restrictToChannels={forAds ? ['facebook'] : []}
            showOnlyNameAttributes={forAds}
          />
        </PathGraphContext.Provider>
      </DragDropContext>
    </MMdndContext.Provider>
  );
};
